// Scoreboard (hold Tab / View, §14.2): rows of glyph, name, match points, round KOs, status
// (alive / ghost / out with its place), ping; the header shows the round x/N, mode and map.
// Touch players tap the mode widget to toggle it.
import { MAX_BEANS, TICK_HZ } from '@shared/constants';
import { BS, ModeId } from '@shared/ids';
import type { PlayerDTO } from '@shared/protocol/messages';
import type { BeanView, FrameView } from '../../game/FrameView';
import type { UiDeps } from '../deps';
import { icon, identityBadge } from '../icons';
import { MODE_NAMES, mapName, ordinal } from '../labels';
import { el, setHidden, setText, toggleClass } from '../ui';
import { pingBars } from '../widgets';

export interface ScoreRow { slot: number; name: string; identity: number; score: number; kos: number; status: string; statusKind: 'alive' | 'ghost' | 'out' | 'away'; ping: number; isBot: boolean; isHost: boolean; me: boolean }

/** Scoreboard rows, best first: match points, then round KOs, then slot (§14.2). */
export function scoreRows(view: FrameView, players: readonly (PlayerDTO | null)[] | null, roundKos: readonly number[]): ScoreRow[] {
  const rows: ScoreRow[] = [];
  for (let i = 0; i < MAX_BEANS; i++) {
    const b: BeanView = view.beans[i];
    const p = players ? players[i] ?? null : null;
    if (!b.active && !p) continue;
    let status = 'ALIVE';
    let kind: ScoreRow['statusKind'] = 'alive';
    if (p && !p.isBot && !p.connected) {
      status = 'AWAY';
      kind = 'away';
    } else if (b.ghost) {
      status = b.place > 0 ? `GHOST • ${ordinal(b.place)}` : 'GHOST';
      kind = 'ghost';
    } else if (b.out) {
      status = b.place > 0 ? `OUT • ${ordinal(b.place)}` : 'OUT';
      kind = 'out';
    } else if (view.modeId === ModeId.CROWN_GRAB) {
      // Crown Grab respawns: nobody is "alive" or "out"; the race is crown time (§4.3).
      if (b.state === BS.RESPAWN || b.state === BS.FALLING) {
        status = 'RESPAWNING';
        kind = 'ghost';
      } else {
        status = view.mode.crownHolder === i ? `HAS CROWN • ${(b.crownTicks / TICK_HZ).toFixed(1)}s` : `CROWN ${(b.crownTicks / TICK_HZ).toFixed(1)}s`;
      }
    }
    rows.push({
      slot: i, name: p?.name ?? (b.name || `Bean ${i + 1}`), identity: p?.identity ?? b.identity, score: p?.score ?? b.score,
      kos: roundKos[i] ?? 0, status, statusKind: kind, ping: p?.ping ?? 0, isBot: p?.isBot ?? b.isBot, isHost: p?.isHost ?? false, me: i === view.localSlot,
    });
  }
  return rows.sort((a, b) => b.score - a.score || b.kos - a.kos || a.slot - b.slot);
}

export interface Scoreboard {
  readonly el: HTMLElement;
  toggle(): void;
  update(view: FrameView, held: boolean, roundKos: readonly number[]): void;
}

export function createScoreboard(deps: UiDeps): Scoreboard {
  const title = el('div', { class: 'sb-title' });
  const sub = el('div', { class: 'sb-sub' });
  const body = el('div', { class: 'sb-rows' });
  const root = el('div', { class: 'panel scoreboard', attrs: { role: 'dialog', 'aria-label': 'Scoreboard' } },
    el('div', { class: 'ribbon yellow sb-ribbon', text: 'SCOREBOARD' }),
    el('div', { class: 'sb-head' }, title, sub),
    el('div', { class: 'sb-cols' }, el('span', { text: '' }), el('span', { text: 'BEAN' }), el('span', { text: 'PTS' }), el('span', { text: 'KOs' }), el('span', { text: 'STATUS' }), el('span', { text: 'PING' })),
    body);
  root.hidden = true;
  let pinned = false;
  let key = '';
  let wasOpen = false;

  return {
    el: root,
    toggle() {
      pinned = !pinned;
    },
    update(view, held, roundKos) {
      const open = held || pinned;
      setHidden(root, !open);
      if (open && !wasOpen) {
        root.classList.remove('open');
        void root.offsetWidth;
        root.classList.add('open');
      }
      wasOpen = open;
      if (!open) return;
      const scoring = view.modeId === ModeId.ROYALE || view.modeId === ModeId.HOT_POTATO || view.modeId === ModeId.CROWN_GRAB;
      setText(title, view.golden ? 'GOLDEN ROUND' : view.totalRounds > 0 ? `ROUND ${view.roundIndex + 1}/${view.totalRounds}` : 'PRACTICE');
      toggleClass(title, 'gold', view.golden);
      setText(sub, scoring ? `${MODE_NAMES[view.modeId]} • ${mapName(view.mapId)}` : mapName(view.mapId));
      const players = deps.room.dto?.players ?? null;
      const rows = scoreRows(view, players, roundKos);
      const colorblind = deps.settings().accessibility.colorblind;
      const k = rows.map((r) => `${r.slot}:${r.score}:${r.kos}:${r.status}:${r.ping > 180 ? 3 : r.ping > 120 ? 2 : 1}:${r.identity}`).join('|') + colorblind;
      if (k === key) return;
      key = k;
      body.replaceChildren(...rows.map((r, i) => el('div', { class: `sb-row ${r.statusKind}${r.me ? ' me' : ''}` },
        el('span', { class: `sb-rank r${i + 1}`, text: String(i + 1) }),
        el('span', { class: 'sb-who' }, identityBadge(r.identity, colorblind, 'idbadge sm'), el('span', { class: 'sb-name ellipsis', text: r.name }),
          r.isHost ? el('span', { class: 'sb-mini host', attrs: { title: 'Host' } }, icon('crown')) : null,
          r.isBot ? el('span', { class: 'sb-mini bot', attrs: { title: 'Bot' } }, icon('bot')) : null),
        el('span', { class: 'sb-pts', text: String(r.score) }),
        el('span', { class: 'sb-kos', text: String(r.kos) }),
        el('span', { class: `sb-status ${r.statusKind}`, text: r.status }),
        el('span', { class: 'sb-ping' }, r.isBot || view.source !== 'online' ? el('span', { class: 'sb-dash', text: '—' }) : pingBars(r.ping)))));
    },
  };
}
